"use client";

import React, { useMemo } from "react";
import type { EChartsOption } from "echarts";
import BaseChart, { type BaseChartRef } from "./base-chart";
import {
  chartTitle,
  chartTooltip,
  chartLegend,
  chartColor,
  chartColors,
} from "./chart-utils";
import { resolveChartTokens } from "@/utils/chart-theme";
import { cn } from "@/src/utils/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PieChartData {
  items: {
    name: string;
    value: number;
    color?: string;
  }[];
}

export interface PieChartProps {
  /** Data to display */
  data: PieChartData;
  /** Chart title */
  title?: string;
  /** Chart subtitle */
  subtitle?: string;
  /** Chart height — CSS value or number (px). Default "100%" */
  height?: string | number;
  /** Extra class names for the wrapper */
  className?: string;
  /** Show legend. Default: true */
  showLegend?: boolean;
  /** Show labels on slices (name + percentage). Default: true */
  showLabels?: boolean;
  /** Show percentage in labels. Default: true */
  showPercentage?: boolean;
  /** Nightingale/rose chart mode where radius varies with value. Default: false */
  roseType?: boolean;
  /** Show loading spinner. Default: false */
  loading?: boolean;
  /** Show empty state. Default: false */
  empty?: boolean;
  /** Callback when a slice is clicked */
  onSliceClick?: (params: { name: string; value: number; percent: number }) => void;
  /** Custom value formatter */
  formatValue?: (value: number) => string;
  /** Accessible label for screen readers */
  ariaLabel?: string;
}

// ---------------------------------------------------------------------------
// PieChart Component
// ---------------------------------------------------------------------------

export const PieChart = React.forwardRef<BaseChartRef, PieChartProps>(
  function PieChart(
    {
      data,
      title,
      subtitle,
      height = "100%",
      className,
      showLegend = true,
      showLabels = true,
      showPercentage = true,
      roseType = false,
      loading = false,
      empty = false,
      onSliceClick,
      formatValue,
      ariaLabel,
    },
    ref
  ) {
    const option = useMemo(() => {
      // Resolve tokens to actual hex values (canvas can't use CSS vars)
      const tokens = resolveChartTokens();

      // Calculate total for percentage
      const total = data.items.reduce((sum, item) => sum + item.value, 0);

      // Build pie data with resolved colors
      const pieData = data.items.map((item, idx) => {
        const percent = total > 0 ? ((item.value / total) * 100).toFixed(1) : "0";
        return {
          name: item.name,
          value: item.value,
          itemStyle: {
            color: item.color || chartColor(idx),
            borderColor: tokens.backgroundColor,
            borderWidth: 3,
            borderRadius: 4,
          },
          label: {
            show: showLabels,
            formatter: () => {
              if (showPercentage) {
                return `{name|${item.name}}\n{percent|${percent}%}`;
              }
              return `{name|${item.name}}`;
            },
            rich: {
              name: {
                fontSize: 12,
                fontWeight: 600,
                fontFamily: tokens.fontFamily,
                color: tokens.textColor,
                lineHeight: 18,
              },
              percent: {
                fontSize: 11,
                fontFamily: tokens.fontFamily,
                color: tokens.textColorSecondary,
                lineHeight: 16,
              },
            },
            distance: 24,
            edgeDistance: 10,
          },
          labelLine: {
            show: showLabels,
            lineStyle: {
              color: tokens.borderColor,
              width: 1,
            },
            length: 16,
            length2: 12,
            smooth: 0.2,
          },
        };
      });

      const baseOption: EChartsOption = {
        ...(title && chartTitle(title, { subtitle })),
        ...chartTooltip({
          trigger: "item",
          formatter: (params: any) => {
            if (!params || !params.name) return "";
            const value = formatValue ? formatValue(params.value) : params.value.toLocaleString();
            return `<div style="margin: 0; padding: 0;">
              <div style="font-weight: 600; margin-bottom: 4px;">${params.name}</div>
              <div><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${params.color};margin-right:6px;"></span>Value: ${value}</div>
              <div style="color: ${tokens.textColorSecondary}; font-size: 11px; margin-top: 4px;">${params.percent}%</div>
            </div>`;
          },
        }),
        ...(showLegend && chartLegend({ position: "bottom" })),
        series: [
          {
            type: "pie",
            name: "",
            data: pieData,
            // Slight inner radius for modern look; larger for rose mode
            radius: roseType ? ["20%", "70%"] : ["8%", "70%"],
            roseType: roseType ? ("radius" as const) : undefined,
            center: ["50%", showLegend ? "46%" : "50%"],
            // Emphasis — NO scale (prevents flickering), shadow + border only
            emphasis: {
              itemStyle: {
                shadowColor: "rgba(0, 0, 0, 0.25)",
                shadowBlur: 16,
                shadowOffsetX: 0,
                shadowOffsetY: 4,
                
              },
              // Disable scaling to prevent hover flickering
              scale: false,
              scaleSize: 0,
            },
            // Stagger animation for premium feel
            animationType: "expansion",
            animationDuration: 1000,
            animationEasing: "cubicInOut",
            animationDelay: (idx: number) => idx * 60,
            // Prevent label overlap
            avoidLabelOverlap: true,
            // Padding angle between slices
            padAngle: 2,
            // Smooth label transitions
            labelLayout: {
              hideOverlap: true,
            },
          },
        ],
      };

      return baseOption;
    }, [
      data,
      title,
      subtitle,
      showLegend,
      showLabels,
      showPercentage,
      roseType,
      formatValue,
    ]);

    const onEvents = useMemo(
      () => ({
        ...(onSliceClick && {
          click: (params: any) => {
            if (params.name && params.value !== undefined) {
              const total = data.items.reduce((sum, item) => sum + item.value, 0);
              const percent = total > 0 ? (params.value / total) * 100 : 0;
              onSliceClick({
                name: params.name,
                value: params.value,
                percent: Math.round(percent * 10) / 10,
              });
            }
          },
        }),
      }),
      [data.items, onSliceClick]
    );

    // Generate data summary for accessibility
    const dataSummary = useMemo(() => {
      const total = data.items.reduce((sum, item) => sum + item.value, 0);
      return data.items.map(item => {
        const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : "0";
        const val = formatValue ? formatValue(item.value) : item.value.toLocaleString();
        return `${item.name}: ${val} (${pct}%)`;
      }).join(", ");
    }, [data, formatValue]);

    return (
      <BaseChart
        ref={ref}
        option={option}
        height={height}
        className={cn("pie-chart", className)}
        loading={loading}
        empty={empty}
        onEvents={Object.keys(onEvents).length > 0 ? onEvents : undefined}
        ariaLabel={ariaLabel || `Pie chart${title ? `: ${title}` : ""} with ${data.items.length} segments`}
        dataSummary={dataSummary}
      />
    );
  }
);

export default PieChart;
